import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, User, Mail, Phone, MapPin, Check, ArrowRight, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

const EVENT_COLORS = [
  { bg: 'bg-indigo-500', light: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-500' },
  { bg: 'bg-emerald-500', light: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-500' },
  { bg: 'bg-rose-500', light: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-500' },
  { bg: 'bg-amber-500', light: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-500' },
  { bg: 'bg-cyan-500', light: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-500' },
];

const PublicCalendarPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0); // 0: Select event, 1: Select date, 2: Select time, 3: Enter details, 4: Confirmation
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [availableDates, setAvailableDates] = useState(null); // Set<'YYYY-MM-DD'> with open slots (null = not loaded)
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // V41.1 — Self-service reschedule (from the email link ?reschedule=<booking_id>)
  const [rescheduleBookingId, setRescheduleBookingId] = useState(null);
  const [rescheduleInfo, setRescheduleInfo] = useState(null);

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_country: 'India',
    customer_city: '',
    notes: '',
    utm_source: searchParams.get('utm_source') || '',
    utm_campaign: searchParams.get('utm_campaign') || ''
  });
  const [customAnswers, setCustomAnswers] = useState({});

  useEffect(() => {
    fetchEventTypes();
  }, []);

  // V41.1 — If the URL carries ?reschedule=<booking_id>, load that booking and
  // jump straight to date/time selection (skip event + details steps).
  useEffect(() => {
    const rid = searchParams.get('reschedule');
    if (!rid) return;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/calendar/public/booking/${rid}`);
        const bk = res.data;
        setRescheduleBookingId(rid);
        setRescheduleInfo(bk);
        if (bk.event_slug) {
          const full = await fetchEventDetails(bk.event_slug);
          if (full) setSelectedEvent(full);
        }
        setStep(1);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'This booking can no longer be rescheduled.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (slug && eventTypes.length > 0) {
      const event = eventTypes.find(e => e.slug === slug);
      if (event) {
        // Fetch full event details including holidays
        fetchEventDetails(slug).then(fullEvent => {
          if (fullEvent) {
            setSelectedEvent(fullEvent);
          } else {
            setSelectedEvent(event);
          }
          setStep(1);
        });
      }
    }
  }, [slug, eventTypes]);

  // Determine the actually-bookable dates (dates that have open slots) for this
  // event, then open the calendar on the month of the EARLIEST bookable date.
  // ROOT CAUSE this fixes: the current month can have no open slots left (e.g.
  // at month-end the day's slots are past / day is disabled), so the first
  // available day is in the NEXT month — previously the calendar opened on the
  // current month and the user had to manually page forward to reach it
  // (e.g. "go to next page to book 1st July").
  useEffect(() => {
    if (!selectedEvent) return;
    let cancelled = false;
    setAvailableDates(null);
    (async () => {
      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const maxDate = new Date(today);
        maxDate.setDate(today.getDate() + (selectedEvent.advance_booking_days || 14));
        const res = await axios.get(
          `${API_URL}/api/calendar/public/available-slots?event_type_id=${selectedEvent.event_type_id}&date_from=${formatDate(today)}&date_to=${formatDate(maxDate)}`,
          { timeout: 12000, headers: { 'Cache-Control': 'no-cache' } }
        );
        if (cancelled) return;
        const dates = new Set((res.data.slots || []).map(s => s.date));
        setAvailableDates(dates);
        const sorted = [...dates].sort();
        if (sorted.length > 0) {
          const [y, m] = sorted[0].split('-').map(Number);
          setCurrentMonth(new Date(y, m - 1, 1));
        }
      } catch (e) {
        if (!cancelled) setAvailableDates(null); // fall back to window-based availability
      }
    })();
    return () => { cancelled = true; };
  }, [selectedEvent]);

  const fetchEventTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/calendar/public/event-types`);
      setEventTypes(response.data);
      
      // If only one event type, auto-select it
      if (response.data.length === 1) {
        setSelectedEvent(response.data[0]);
        setStep(1);
      }
    } catch (error) {
      console.error('Error fetching event types:', error);
      toast.error('Failed to load booking options');
    } finally {
      setLoading(false);
    }
  };

  // Fetch full event details including holidays
  const fetchEventDetails = async (slug) => {
    try {
      const response = await axios.get(`${API_URL}/api/calendar/public/event/${slug}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching event details:', error);
      return null;
    }
  };

  const fetchSlots = async (date, retryCount = 0) => {
    if (!selectedEvent || !date) return;

    setLoading(true);
    try {
      const dateStr = formatDate(date);
      const response = await axios.get(
        `${API_URL}/api/calendar/public/available-slots?event_type_id=${selectedEvent.event_type_id}&date_from=${dateStr}&date_to=${dateStr}`,
        { 
          timeout: 10000,
          headers: { 'Cache-Control': 'no-cache' }
        }
      );
      console.log('Slots response:', response.data); // Debug log
      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
      // Retry once on failure
      if (retryCount < 1) {
        console.log('Retrying slot fetch...');
        await new Promise(r => setTimeout(r, 1000));
        return fetchSlots(date, retryCount + 1);
      }
      toast.error('Failed to load available times. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    // Ensure we get the correct local date regardless of timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleEventSelect = async (event) => {
    // Fetch full event details including holidays
    const fullEvent = await fetchEventDetails(event.slug);
    if (fullEvent) {
      setSelectedEvent(fullEvent);
    } else {
      setSelectedEvent(event);
    }
    setStep(1);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    fetchSlots(date);
    setStep(2);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    if (rescheduleBookingId) {
      handleConfirmReschedule(slot);
    } else {
      setStep(3);
    }
  };

  const handleConfirmReschedule = async (slot) => {
    if (!rescheduleBookingId || !selectedDate || !slot) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/calendar/public/reschedule/${rescheduleBookingId}`,
        { booking_date: formatDate(selectedDate), booking_time: slot.start_time }
      );
      setBookingSuccess(res.data);
      setStep(4);
      toast.success('Your consultation has been rescheduled!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reschedule');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedEvent || !selectedSlot) {
      toast.error('Please select a time slot');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/calendar/public/book`, {
        event_type_id: selectedEvent.event_type_id,
        slot_id: selectedSlot.slot_id,
        ...formData,
        custom_answers: customAnswers
      });

      setBookingSuccess(response.data);
      setStep(4);
      toast.success('Consultation booked successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book consultation');
    } finally {
      setSubmitting(false);
    }
  };

  // Calendar helpers
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    const prevMonthLastDay = new Date(year, month, 0).getDate();

    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, prevMonthLastDay - i), isCurrentMonth: false });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }

    return days;
  };

  const isDateAvailable = (date) => {
    if (!selectedEvent) return false;
    const dateStr = formatDate(date);

    // Slot-driven availability is AUTHORITATIVE once loaded: a date is bookable
    // only if it actually has open slots (already excludes past times, the
    // minimum-notice window, holidays and fully-booked days). This prevents
    // showing days like a slot-less month-end date as clickable.
    if (availableDates) return availableDates.has(dateStr);

    // Fallback (slots not loaded yet): window + notice + holiday check.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minNotice = selectedEvent.minimum_notice_hours ?? 24;
    const earliest = new Date(Date.now() + minNotice * 3600 * 1000);
    earliest.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + (selectedEvent.advance_booking_days || 14));
    if ((selectedEvent.holidays || []).includes(dateStr)) {
      return false;
    }
    return date >= earliest && date <= maxDate;
  };

  const isHoliday = (date) => {
    if (!selectedEvent) return false;
    const dateStr = formatDate(date);
    const holidays = selectedEvent.holidays || [];
    return holidays.includes(dateStr);
  };

  const isToday = (date) => date.toDateString() === new Date().toDateString();

  const days = getDaysInMonth();
  const colorScheme = selectedEvent ? EVENT_COLORS[selectedEvent.color || 0] : EVENT_COLORS[0];

  if (loading && eventTypes.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_doc-insights-24/artifacts/8eif3nnf_Logo.png"
            alt="Soul Sanskar"
            className="h-10 object-contain"
          />
          <div>
            <h1 className="text-lg font-bold text-slate-900">Soul Sanskar</h1>
            <p className="text-xs text-slate-500" data-testid="booking-page-subtitle">{selectedEvent?.page_subtitle || 'Book Your Free Consultation'}</p>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* V41.1 — Reschedule banner */}
        {rescheduleBookingId && step !== 4 && (
          <div className="mb-6 rounded-xl border border-indigo-200 bg-indigo-50 px-5 py-4 flex items-start gap-3" data-testid="reschedule-banner">
            <Calendar className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-indigo-900">
              <p className="font-semibold">Rescheduling your booking</p>
              <p className="text-indigo-700">
                {rescheduleInfo?.event_name ? `${rescheduleInfo.event_name} — ` : ''}
                currently {rescheduleInfo?.booking_date} at {rescheduleInfo?.booking_time ? formatTime(rescheduleInfo.booking_time) : ''}. Pick a new date & time below.
              </p>
            </div>
          </div>
        )}
        {/* Step 0: Select Event Type */}
        {step === 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 animate-fade-in">
            <div className="text-center mb-8">
              <Calendar className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Choose a Consultation</h2>
              <p className="text-slate-500">Select the type of consultation you&apos;d like to book</p>
            </div>

            <div className="grid gap-4 max-w-lg mx-auto">
              {eventTypes.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <p>No consultation types available.</p>
                  <button 
                    onClick={fetchEventTypes}
                    className="mt-4 text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Try again
                  </button>
                </div>
              ) : (
                eventTypes.map((event) => (
                  <button
                    key={event.event_type_id}
                    onClick={() => handleEventSelect(event)}
                    className="bg-white border-2 border-slate-200 hover:border-indigo-500 rounded-xl p-5 text-left transition-all hover:shadow-md group"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-3 h-12 rounded-full ${EVENT_COLORS[event.color || 0].bg}`} />
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 group-hover:text-indigo-600">{event.name}</h3>
                        <p className="text-sm text-slate-500">{event.duration_minutes} minutes</p>
                        {event.country_filter && (
                          <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${EVENT_COLORS[event.color || 0].light} ${EVENT_COLORS[event.color || 0].text}`}>
                            {event.country_filter}
                          </span>
                        )}
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-indigo-600" />
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* Step 1: Select Date */}
        {step === 1 && selectedEvent && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden animate-fade-in">
            <div className={`h-2 ${colorScheme.bg}`} />
            <div className="p-8">
              <button onClick={() => setStep(0)} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6">
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">{selectedEvent.name}</h2>
                <p className="text-slate-500">{selectedEvent.duration_minutes} min • Select a date</p>
              </div>

              {/* Calendar */}
              <div className="max-w-md mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="p-2 hover:bg-slate-100 rounded-lg">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="font-semibold text-slate-900">
                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </span>
                  <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="p-2 hover:bg-slate-100 rounded-lg">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-7 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                    <div key={d} className="text-center text-xs font-medium text-slate-500 py-2">{d}</div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {days.map((day, idx) => {
                    const available = isDateAvailable(day.date) && day.isCurrentMonth;
                    const holiday = isHoliday(day.date) && day.isCurrentMonth;
                    return (
                      <button
                        key={idx}
                        onClick={() => available && handleDateSelect(day.date)}
                        disabled={!available}
                        title={holiday ? 'Holiday - Not available' : ''}
                        className={`aspect-square flex items-center justify-center text-sm rounded-lg transition-all ${
                          !day.isCurrentMonth ? 'text-slate-300' :
                          holiday ? 'bg-amber-100 text-amber-400 cursor-not-allowed line-through' :
                          !available ? 'text-slate-300 cursor-not-allowed' :
                          isToday(day.date) ? `${colorScheme.bg} text-white font-medium` :
                          `hover:${colorScheme.light} hover:${colorScheme.text} font-medium`
                        }`}
                      >
                        {day.date.getDate()}
                      </button>
                    );
                  })}
                </div>

                <p className="text-center text-sm text-slate-500 mt-4">
                  You can book up to {selectedEvent.advance_booking_days || 14} days in advance
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Select Time */}
        {step === 2 && selectedEvent && selectedDate && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden animate-fade-in">
            <div className={`h-2 ${colorScheme.bg}`} />
            <div className="p-8">
              <button onClick={() => setStep(1)} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6">
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <div className="text-center mb-8">
                <Clock className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Select a Time</h2>
                <p className="text-slate-500">
                  {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
              </div>

              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-slate-500 mb-4">No available slots for this date.</p>
                  <button onClick={() => setStep(1)} className="text-indigo-600 hover:text-indigo-700 font-medium">
                    Choose another date
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-w-lg mx-auto">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot.slot_id}
                      onClick={() => handleSlotSelect(slot)}
                      className={`p-4 rounded-xl border-2 border-slate-200 hover:${colorScheme.border} hover:${colorScheme.light} transition-all text-center`}
                    >
                      <div className="text-lg font-semibold text-slate-900">{formatTime(slot.start_time)}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Enter Details */}
        {step === 3 && selectedEvent && selectedSlot && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden animate-fade-in">
            <div className={`h-2 ${colorScheme.bg}`} />
            <div className="p-8">
              <button onClick={() => setStep(2)} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6">
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <div className="text-center mb-8">
                <User className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Your Details</h2>
                <p className="text-slate-500">
                  {selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at {formatTime(selectedSlot.start_time)}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      required
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter your full name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      required
                      value={formData.customer_email}
                      onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="tel"
                      required
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="+91 XXXXXXXXXX"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
                    <select
                      value={formData.customer_country}
                      onChange={(e) => setFormData({ ...formData, customer_country: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="India">India</option>
                      <option value="United States">United States</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="Canada">Canada</option>
                      <option value="Australia">Australia</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
                    <input
                      type="text"
                      value={formData.customer_city}
                      onChange={(e) => setFormData({ ...formData, customer_city: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Your city"
                    />
                  </div>
                </div>

                {selectedEvent?.custom_questions?.length > 0 && (
                  <div className="space-y-4" data-testid="custom-questions">
                    {selectedEvent.custom_questions.map((q) => (
                      <div key={q.key}>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          {q.label}{q.required && <span className="text-rose-500"> *</span>}
                        </label>
                        {q.type === 'select' ? (
                          <select
                            required={q.required}
                            value={customAnswers[q.key] || ''}
                            onChange={(e) => setCustomAnswers({ ...customAnswers, [q.key]: e.target.value })}
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            data-testid={`cq-${q.key}`}
                          >
                            <option value="">Select…</option>
                            {(q.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        ) : (
                          <input
                            type={q.type === 'number' ? 'number' : q.type === 'date' ? 'date' : 'text'}
                            required={q.required}
                            value={customAnswers[q.key] || ''}
                            onChange={(e) => setCustomAnswers({ ...customAnswers, [q.key]: e.target.value })}
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            data-testid={`cq-${q.key}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full ${colorScheme.bg} text-white py-4 rounded-lg font-semibold hover:opacity-90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50`}
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Booking...
                    </>
                  ) : (
                    <>
                      {selectedEvent?.cta_text || 'Book Consultation'}
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && bookingSuccess && (
          <div className="bg-white rounded-2xl shadow-lg p-8 animate-fade-in text-center">
            <div className={`w-20 h-20 ${colorScheme.light} rounded-full flex items-center justify-center mx-auto mb-6`}>
              <Check className={`w-10 h-10 ${colorScheme.text}`} />
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-testid="confirmation-title">{rescheduleBookingId ? 'Booking Rescheduled!' : (selectedEvent?.confirmation_title || 'Booking Confirmed!')}</h2>
            <p className="text-slate-500 mb-8" data-testid="confirmation-subtitle">{rescheduleBookingId ? 'Your consultation has been moved to the new time.' : (selectedEvent?.confirmation_subtitle || 'Your prenatal consultation has been scheduled successfully.')}</p>

            <div className="bg-slate-50 rounded-xl p-6 max-w-md mx-auto mb-8">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Event</span>
                  <span className="font-medium text-slate-900">{bookingSuccess.details?.event_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Date</span>
                  <span className="font-medium text-slate-900">{bookingSuccess.details?.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Time</span>
                  <span className="font-medium text-slate-900">{formatTime(bookingSuccess.details?.time || '')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Booking ID</span>
                  <span className="font-mono text-sm text-slate-700">{bookingSuccess.booking_id}</span>
                </div>
              </div>
            </div>

            <div className={`${colorScheme.light} rounded-xl p-6 max-w-md mx-auto`}>
              <h3 className={`font-semibold ${colorScheme.text} mb-2`}>{selectedEvent?.whats_next_title || "What's Next?"}</h3>
              <ul className={`text-sm ${colorScheme.text} space-y-2 text-left`} data-testid="whats-next-list">
                {(selectedEvent?.whats_next_items?.length
                  ? selectedEvent.whats_next_items
                  : [
                      "You'll receive a confirmation email shortly",
                      'Our team will call you at the scheduled time',
                    ]
                ).map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* V41 — Confirmation CTA buttons (e.g. Join WhatsApp Group) */}
            {selectedEvent?.confirmation_buttons?.length > 0 && (
              <div className="flex flex-col items-center gap-3 mt-6" data-testid="confirmation-buttons">
                {selectedEvent.confirmation_buttons.map((btn, idx) => (
                  <a
                    key={btn.id || idx}
                    href={btn.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-block px-6 py-3 rounded-xl font-semibold text-white ${colorScheme.bg} hover:opacity-90 transition-opacity`}
                    data-testid={`confirmation-button-${idx}`}
                  >
                    {btn.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <footer className="text-center py-8 text-slate-500 text-sm">
        <p>&copy; {new Date().getFullYear()} Soul Sanskar Healthcare Private Limited</p>
      </footer>
    </div>
  );
};

export default PublicCalendarPage;
